import { useState, useEffect } from 'react';
import { useTaskStore } from '@/lib/task-store';
import { OneOnOneMeeting } from '@/lib/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils/timezone';
import { Calendar, Clock, Plus, Users, X } from 'lucide-react';
import { useMeetingStore } from '@/lib/meeting-store';
import { TaskForm } from '@/components/tasks/task-form';
import { MeetingStopwatch } from './meeting-stopwatch';
import { supabase } from '@/lib/supabase';

interface MeetingDetailsProps {
  meeting: OneOnOneMeeting;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MeetingDetails({ meeting, open, onOpenChange }: MeetingDetailsProps) {
  const { createTask, tasks, loading, error: taskError, fetchTasks } = useTaskStore();
  const { updateMeeting, createAgendaItem, updateAgendaItem } = useMeetingStore();
  const [currentAgendaItem, setCurrentAgendaItem] = useState<string | null>(null);
  const [meetingData, setMeetingData] = useState<CompletedMeetingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [formData, setFormData] = useState({
    type: [] as AgendaItemType[],
    discussion_points: meeting.discussion_points || '',
    next_steps: meeting.next_steps || '',
    blockers: meeting.blockers || '',
    notes: meeting.notes || '',
  });

  const relevantTasks = tasks.filter((task) => task.owner_id === meeting.team_member_id);

  const handleTaskClick = (task: any) => {
    const taskInfo = `\n${task.title}\nStatus: ${task.status}\nPriority: ${task.priority}\nDescription: ${task.description || 'No description'}\n`;
    setFormData(prev => ({
      ...prev,
      discussion_points: prev.discussion_points ? prev.discussion_points + taskInfo : taskInfo
    }));
  };

  useEffect(() => {
    const fetchMeetingData = async () => {
      try {
        // Get meeting data
        const { data: meetingData, error: meetingError } = await supabase
          .from('one_on_one_meetings')
          .select(`
            id,
            status
          `)
          .eq('id', meeting.id)
          .single();

        if (meetingError) throw meetingError;

        // Get agenda data
        const { data: agendaData, error: agendaError } = await supabase
          .from('meeting_agenda_items')
          .select(`
            id,
            type,
            discussion_points,
            next_steps,
            blockers,
            notes
          `)
          .eq('meeting_id', meeting.id)
          .single();

        if (agendaError) throw agendaError;

        setMeetingData({
          ...meetingData,
          discussion_points: agendaData?.discussion_points,
          next_steps: agendaData?.next_steps,
          blockers: agendaData?.blockers,
          notes: agendaData?.notes
        });
      } catch (err) {
        console.error('Error fetching meeting data:', err);
        setError('Failed to load meeting data');
      }
    };

    if (meeting.id && meeting.status === 'completed') {
      fetchMeetingData();
    }
  }, [meeting.id, meeting.status]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      let agendaItemId = currentAgendaItem;
      
      // If no agenda item exists, create one
      if (!agendaItemId) {
        const agendaItem = await createAgendaItem({
          meeting_id: meeting.id,
          type: formData.type,
          discussion_points: formData.discussion_points,
          next_steps: formData.next_steps,
          blockers: formData.blockers,
          notes: formData.notes
        });
        agendaItemId = agendaItem.id;
        setCurrentAgendaItem(agendaItemId);
      } else {
        // Update existing agenda item
        await updateAgendaItem(agendaItemId, {
          type: formData.type,
          discussion_points: formData.discussion_points,
          next_steps: formData.next_steps,
          blockers: formData.blockers,
          notes: formData.notes
        });
      }

      // Only update meeting status
      await updateMeeting(meeting.id, { status: 'completed' });

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving meeting:', error);
      setError('Failed to save meeting');
    }
  };

  const handleMeetingComplete = async () => {
    if (!currentAgendaItem) return;
    
    try {
      // Update agenda item
      await updateAgendaItem(currentAgendaItem, {
        type: formData.type,
        discussion_points: formData.discussion_points,
        next_steps: formData.next_steps,
        blockers: formData.blockers,
        notes: formData.notes
      });

      // Update meeting status
      await updateMeeting(meeting.id, { status: 'completed' });
    } catch (error) {
      console.error('Error completing meeting:', error);
      setError('Failed to complete meeting');
    }
  };

  const handleMeetingStart = async () => {
    try {
      await updateMeeting(meeting.id, { status: 'in_progress' });
    } catch (error) {
      console.error('Error starting meeting:', error);
      setError('Failed to start meeting');
    }
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 bg-gray-900 text-gray-100">
        {error && (
          <div className="absolute top-0 left-0 right-0 p-2 bg-red-600 text-white text-center">
            {error}
          </div>
        )}
        <div className="h-full flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0 p-6 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle>Meeting Details</DialogTitle>
                <div className="flex items-center gap-4 text-sm text-gray-400 mt-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(meeting.meeting_date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{meeting.duration_minutes} minutes</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{meeting.team_member?.name}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-6">
                {meeting.status === 'scheduled' && (
                  <Button 
                    variant="default"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleMeetingStart}
                  >
                    Start Meeting
                  </Button>
                )}
                {meeting.status === 'in_progress' && (
                  <MeetingStopwatch 
                    durationMinutes={meeting.duration_minutes}
                    onComplete={handleMeetingComplete}
                  />
                )}
                <X 
                  className="h-6 w-6 cursor-pointer opacity-70 transition-opacity hover:opacity-100" 
                  onClick={() => onOpenChange(false)} 
                />
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
            {meeting.status === 'completed' ? (
              // Display completed meeting data
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Meeting Summary</h3>
                  <div className="space-y-4">
                    <div>
                      <h4>Discussion Points</h4>
                      <p>{meetingData?.discussion_points}</p>
                    </div>
                    <div>
                      <h4>Next Steps</h4>
                      <p>{meetingData?.next_steps}</p>
                    </div>
                    {meetingData?.blockers && (
                      <div>
                        <h4>Blockers</h4>
                        <p>{meetingData.blockers}</p>
                      </div>
                    )}
                    {meetingData?.notes && (
                      <div>
                        <h4>Notes</h4>
                        <p>{meetingData.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Edit form for non-completed meetings
              <div className="space-y-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Type</label>
                    <select
                      multiple
                      value={formData.type}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        type: Array.from(e.target.selectedOptions, option => option.value as AgendaItemType)
                      })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-sm scrollbar-hide"
                    >
                      <option value="task_review">Task Review</option>
                      <option value="scrum_update">Scrum Update</option>
                      <option value="action_item">Action Item</option>
                      <option value="discussion_point">Discussion Point</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Discussion Points</label>
                    <textarea
                      name="discussion_points"
                      value={formData.discussion_points}
                      onChange={e => setFormData({ ...formData, discussion_points: e.target.value })}
                      className="w-full h-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-sm"
                      placeholder="Enter key points discussed in the meeting"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Next Steps</label>
                    <textarea
                      name="next_steps"
                      value={formData.next_steps}
                      onChange={e => setFormData({ ...formData, next_steps: e.target.value })}
                      className="w-full h-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-sm"
                      placeholder="Document agreed upon next steps"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Blockers (Optional)</label>
                    <textarea
                      name="blockers"
                      value={formData.blockers}
                      onChange={e => setFormData({ ...formData, blockers: e.target.value })}
                      className="w-full h-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-sm"
                      placeholder="Document any blockers or challenges"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Additional Notes (Optional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full h-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-sm"
                      placeholder="Any other relevant notes or observations"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit">Save Meeting Notes</Button>
                  </div>
                </form>

                <div className="border-t border-gray-800 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">Team Member's Tasks</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-gray-100"
                      onClick={() => setShowTaskForm(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New RAIL
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {relevantTasks.length === 0 ? (
                      <div className="text-center py-4 text-gray-400">
                        No tasks found for this team member
                      </div>
                    ) : (
                      relevantTasks.map((task) => (
                        <div
                          key={task.id}
                          className="p-4 rounded-lg border border-gray-700 bg-gray-800 hover:bg-gray-750 transition-colors cursor-pointer"
                          onClick={() => handleTaskClick(task)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="space-y-1 flex-1">
                              <div className="font-medium flex items-center gap-2">
                                <span>{task.title}</span>
                                <span className="text-sm text-gray-400">#{task.id}</span>
                              </div>
                              {task.description && (
                                <p className="text-sm text-gray-400">{task.description}</p>
                              )}
                              <div className="text-sm text-gray-400">
                                Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className={`px-2 py-1 text-xs font-medium rounded-full ${
                                task.status === 'Open'
                                  ? 'bg-blue-900 text-blue-300'
                                  : task.status === 'Active'
                                  ? 'bg-purple-900 text-purple-300'
                                  : 'bg-orange-900 text-orange-300'
                              }`}>
                                {task.status}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showTaskForm && (
          <Dialog open={showTaskForm} onOpenChange={setShowTaskForm}>
            <DialogContent className="max-w-4xl bg-[#1a1f2e] text-gray-100 p-0 border-0">
              <div className="p-6">
                <TaskForm />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
